'use client';
import { Fragment, useContext, useDeferredValue, useMemo } from 'react';
import type { StorefrontCategory } from '@/api/menu-list/storefront-menu-snapshot.types';
import { useStorageValue } from '@/shared/lib/storage';
import { CategoriesContext, CategoriesProvider } from '@/shared/ui/providers/categories-provider';
import { CategoryItemsConfigProvider } from '@/shared/ui/providers/category-items-config-provider';
import { CategoryProvider } from '@/shared/ui/providers/category-provider';
import { CategoriesData, InjectionConfig, ItemsConfig, OverrideConfig } from './types';

type Props = Omit<CategoriesData, 'data'>;

const flattenCategories = (categories: StorefrontCategory[]) => {
    const flat: StorefrontCategory[] = [];
    const walk = (category: StorefrontCategory) => {
        flat.push(category);
        category.children?.forEach(walk);
    };
    categories.forEach(walk);
    return flat;
};

const matchesEntry = (entry: { id?: string; index?: number } | undefined, id: string, index: number) => {
    if (!entry) {
        return false;
    }
    if (entry.id) {
        return entry.id === id;
    }
    if (typeof entry.index === 'number') {
        return entry.index === index;
    }
    return false;
};

const resolveOverride = <T extends OverrideConfig>(overrides: T[] | undefined, id: string, index: number) => {
    if (!overrides || overrides.length === 0) {
        return undefined;
    }
    const byId = overrides.find((override) => override.id && override.id === id);
    if (byId) {
        return byId;
    }
    return overrides.find((override) => typeof override.index === 'number' && override.index === index);
};

const resolveInjections = (
    injections: InjectionConfig[] | undefined,
    id: string,
    index: number,
    position: 'before' | 'after',
) => {
    if (!injections || injections.length === 0) {
        return [];
    }
    return injections
        .filter(Boolean)
        .filter((injection) => (injection.position ?? 'after') === position)
        .filter((injection) => matchesEntry(injection, id, index))
        .map((injection, injectionIndex) => (
            <Fragment key={`${position}-${id}-${index}-${injectionIndex}`}>{injection.component}</Fragment>
        ));
};

const mergeItemsConfig = (base?: ItemsConfig, override?: ItemsConfig): ItemsConfig => ({
    limit: override && 'limit' in override ? override.limit : base?.limit,
    injections: override?.injections ?? base?.injections,
    overrides: override?.overrides ?? base?.overrides,
});

/** Snapshot categories already drop non-public items — hashes present ⇒ showable. */
const hasItems = (category: StorefrontCategory): boolean => {
    if (category.itemHashes.length > 0) return true;
    return category.children?.some(hasItems) ?? false;
};

const getCategoryWithSubcategories = (
    categories: StorefrontCategory[],
    categoryId: string,
): StorefrontCategory[] => {
    const flatCategories = flattenCategories(categories);
    const category = flatCategories.find((cat) => cat.id === categoryId);

    if (!category) {
        return [];
    }

    const result: StorefrontCategory[] = [category];

    const addSubcategories = (cat: StorefrontCategory) => {
        if (cat.children) {
            [...cat.children]
                .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
                .forEach((child) => {
                    result.push(child);
                    addSubcategories(child);
                });
        }
    };

    addSubcategories(category);
    return result;
};

const CategoriesForContent = ({ children, filters, injections, overrides, items }: Props) => {
    const { data } = useContext(CategoriesContext);
    const [categoryIdRaw] = useStorageValue(filters?.categoryId);
    const [subCategoryIdRaw] = useStorageValue(filters?.subCategoryId);
    // Defer the filter values: switching category/subcategory re-renders the (potentially large) item
    // list at low priority instead of blocking the main thread, so the page stays interactive and the
    // previous list stays visible until the new one is ready.
    const categoryId = useDeferredValue(categoryIdRaw);
    const subCategoryId = useDeferredValue(subCategoryIdRaw);

    const filteredCategories = useMemo(() => {
        let result = filters?.includeSubcategories ? flattenCategories(data) : data;

        if (categoryId) {
            result = getCategoryWithSubcategories(data, categoryId);
        }

        if (subCategoryId) {
            const flatCategories = flattenCategories(data);
            result = flatCategories.filter((category) => category.id === subCategoryId);
        }

        if (filters?.onlyWithItems) {
            result = result.filter((category) => hasItems(category));
        }

        return result;
    }, [data, categoryId, subCategoryId, filters?.includeSubcategories, filters?.onlyWithItems]);

    return (
        <>
            {filteredCategories.flatMap((category, index) => {
                const override = resolveOverride(overrides, category.id, index);
                const categoryChildren = override?.children ?? children;
                const itemsConfig = mergeItemsConfig(items, override?.items);
                const before = resolveInjections(injections, category.id, index, 'before');
                const after = resolveInjections(injections, category.id, index, 'after');

                return [
                    ...before,
                    <CategoryProvider key={category.id} value={{ data: category }}>
                        <CategoryItemsConfigProvider value={itemsConfig}>
                            {categoryChildren}
                        </CategoryItemsConfigProvider>
                    </CategoryProvider>,
                    ...after,
                ];
            })}
        </>
    );
};

export const CategoriesFor = (props: Props) => {
    return (
        <CategoriesProvider>
            <CategoriesForContent {...props} />
        </CategoriesProvider>
    );
};
